import { useEffect, useMemo, useState } from 'react';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';
import { useCountry } from '../hooks/useCountry';
import { premiumApi } from '../services/api';
import { formatCurrency } from '../lib/dateFormat';
import {
  UserPlus, Search, Users, Target, BarChart3, Trophy,
  AlertCircle, Award, Crown, Bell,
} from 'lucide-react';
import { SEO } from '../components/SEO';
import { useLocalizedSeo } from '../hooks/useLocalizedSeo';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';
import ScoringFAQ from '../components/ScoringFAQ';
import { usePublicFreeLimits } from '../hooks/usePublicFreeLimits';
import { isSubdomainEnabled, getAppDomain } from '../utils/domain';

const STEPS = [
  { id: 'criar-conta', n: 1, def: 'Criando sua conta' },
  { id: 'entrar-em-bolao', n: 2, def: 'Entrando em um bolão' },
  { id: 'criar-bolao', n: 3, def: 'Criando seu próprio bolão' },
  { id: 'convidar', n: 4, def: 'Convidando amigos' },
  { id: 'palpitar', n: 5, def: 'Fazendo palpites' },
  { id: 'pontuacao', n: 6, def: 'Como funciona a pontuação' },
  { id: 'ranking', n: 7, def: 'Acompanhando o ranking' },
  { id: 'conquistas', n: 8, def: 'Conquistas e estatísticas' },
  { id: 'casos-especiais', n: 9, def: 'Casos especiais' },
  { id: 'premium', n: 10, def: 'Free vs Premium' },
];

export default function ComoJogar() {
  const { t, i18n } = useTranslation('institutional');
  const { limits } = usePublicFreeLimits();
  const { country } = useCountry();
  // Mesma logica da Landing: aponta pro subdominio app quando habilitado.
  // /login redireciona pra /auth (tela de cadastro/login).
  const loginUrl = isSubdomainEnabled() ? `https://${getAppDomain()}/login` : '/login';

  const poolNoun =
    limits.maxPrivatePoolsCreated === 1
      ? t('howtoplay.pool_singular', 'bolão privado')
      : t('howtoplay.pool_plural', 'bolões privados');

  // Preço Premium regional resolvido pelo país (Release D). Default R\$ 9,90
  // como fallback (user BR ou erro de rede).
  const [priceLabel, setPriceLabel] = useState<string>('R$ 9,90');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const plans = await premiumApi.listPlans();
        const defaultPlan = plans.find((p) => p.defaultPlan) ?? plans[0];
        if (!defaultPlan) return;
        const regional = await premiumApi.getPreferredPrice(defaultPlan.id, country);
        if (cancelled || !regional) return;
        setPriceLabel(formatCurrency(regional.priceCents, regional.currency, i18n.language));
      } catch { /* silencioso */ }
    })();
    return () => { cancelled = true; };
  }, [country, i18n.language]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'Como jogar bolão no Bolão Elite — guia passo a passo',
      description:
        'Tutorial completo: criar conta, entrar ou criar um bolão, convidar amigos, palpitar, entender pontuação e acompanhar o ranking.',
      totalTime: 'PT5M',
      inLanguage: 'pt-BR',
      step: [
        { '@type': 'HowToStep', position: 1, name: 'Crie sua conta', text: 'Cadastre-se gratuitamente com e-mail ou Google em menos de um minuto.' },
        { '@type': 'HowToStep', position: 2, name: 'Escolha um bolão', text: 'Crie um bolão privado para amigos ou entre em um bolão público aberto.' },
        { '@type': 'HowToStep', position: 3, name: 'Convide amigos', text: 'Em bolões privados, compartilhe o link de convite pelo WhatsApp.' },
        { '@type': 'HowToStep', position: 4, name: 'Faça palpites', text: 'Antes do início de cada jogo, registre seu palpite de placar exato.' },
        { '@type': 'HowToStep', position: 5, name: 'Acompanhe o ranking', text: 'A pontuação atualiza em tempo real conforme os jogos terminam.' },
      ],
    }),
    []
  );

  const seo = useLocalizedSeo('como-jogar');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SEO
        title={t('howtoplay.seo_title', 'Como jogar bolão no Bolão Elite — Tutorial completo')}
        description={t('howtoplay.seo_desc', 'Guia passo a passo para usar o Bolão Elite: criar conta, entrar ou criar um bolão, convidar amigos, palpitar, ver pontuação e ranking em tempo real.')}
        canonical={seo.canonical}
        ogUrl={seo.ogUrl}
        locale={seo.locale}
        alternates={seo.alternates}
        keywords="como jogar bolão, tutorial bolão, criar bolão, palpitar, pontuação, ranking"
        jsonLd={jsonLd}
      />

      <PublicHeader />

      <section className="bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <nav className="text-sm text-white/70 mb-3" aria-label="breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">{t('common:nav.home', 'Início')}</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{t('howtoplay.breadcrumb', 'Como jogar')}</span>
          </nav>
          <span className="inline-block text-xs font-semibold text-primary-200 uppercase tracking-widest">
            {t('howtoplay.eyebrow', 'Guia completo')}
          </span>
          <h1 className="mt-3 text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            {t('howtoplay.h1', 'Como jogar bolão no Bolão Elite')}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-3xl">
            {t('howtoplay.intro', 'Do cadastro ao primeiro palpite em menos de 5 minutos. Este guia cobre tudo o que você precisa saber para criar seu bolão, convidar amigos, palpitar e acompanhar o ranking — passo a passo, com casos especiais e as diferenças entre o plano gratuito e o Premium.')}
          </p>
        </div>
      </section>

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-10 sm:py-14">
        {/* Sumário / TOC */}
        <nav className="bg-white border border-gray-200 rounded-2xl p-5 mb-12" aria-label={t('howtoplay.toc_aria', 'Sumário')}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">
            {t('howtoplay.toc_label', 'Neste guia')}
          </p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 list-decimal list-inside">
            {STEPS.map(step => (
              <li key={step.id}>
                <a href={`#${step.id}`} className="text-sm text-primary-700 font-semibold hover:underline">
                  {t(`howtoplay.step_${step.n}`, step.def)}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article
          className="
            space-y-12
            [&_h2]:text-2xl sm:[&_h2]:text-3xl [&_h2]:font-extrabold [&_h2]:text-gray-900 [&_h2]:tracking-tight [&_h2]:mb-3 [&_h2]:flex [&_h2]:items-center [&_h2]:gap-3 [&_h2]:scroll-mt-24
            [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3]:mt-6 [&_h3]:mb-2
            [&_p]:text-base [&_p]:text-gray-700 [&_p]:leading-relaxed [&_p]:mb-4
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_ul]:mb-4 [&_ul]:text-gray-700
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_ol]:mb-4 [&_ol]:text-gray-700
            [&_strong]:text-gray-900 [&_strong]:font-bold
          "
        >
          <section id="criar-conta">
            <h2><Icon icon={<UserPlus className="w-6 h-6" />} /> 1. {t('howtoplay.step_1', 'Criando sua conta')}</h2>
            <p>
              <Trans i18nKey="howtoplay.s1_p1" t={t}>
                Tudo começa com uma conta gratuita. No site (<a href="/" className="text-primary-600 font-semibold hover:underline">bolaoelite.com.br</a>) ou no app Android, toque em <strong>"Entrar"</strong> e em seguida em <strong>"Criar conta"</strong>. Você pode se cadastrar de duas formas:
              </Trans>
            </p>
            <ul>
              <li>
                <strong>{t('howtoplay.s1_li1_k', 'E-mail + senha:')}</strong>{' '}
                {t('howtoplay.s1_li1_v', 'informe um e-mail válido (vamos enviar um link de confirmação) e escolha uma senha de pelo menos 6 caracteres.')}
              </li>
              <li>
                <strong>{t('howtoplay.s1_li2_k', 'Login com Google:')}</strong>{' '}
                {t('howtoplay.s1_li2_v', 'um clique e pronto. Mais rápido e seguro — não precisa lembrar de outra senha.')}
              </li>
            </ul>
            <p>{t('howtoplay.s1_p2', 'No primeiro login, complete seu perfil (nome de exibição, foto opcional). Você pode adicionar CPF e telefone depois — só são obrigatórios se for receber premiação em Pix em algum bolão público com concurso cultural.')}</p>
          </section>

          <section id="entrar-em-bolao">
            <h2><Icon icon={<Search className="w-6 h-6" />} /> 2. {t('howtoplay.step_2', 'Entrando em um bolão')}</h2>
            <p>{t('howtoplay.s2_p1', 'Há duas formas de jogar bolão no Bolão Elite, e você pode usar as duas ao mesmo tempo:')}</p>
            <h3>{t('howtoplay.s2_h_private', 'Bolões privados (entre amigos)')}</h3>
            <p>
              <Trans i18nKey="howtoplay.s2_p2" t={t}>
                Para entrar em um bolão privado, você precisa de um <strong>link de convite</strong> enviado pelo criador. É o formato clássico: alguém do seu grupo cria o bolão e compartilha o link no WhatsApp ou Instagram. Você clica, aceita o convite e já está dentro.
              </Trans>
            </p>
            <h3>{t('howtoplay.s2_h_public', 'Bolões públicos (qualquer um pode entrar)')}</h3>
            <p>
              <Trans i18nKey="howtoplay.s2_p3" t={t}>
                Não tem grupo de amigos para jogar? Sem problema. Acesse a seção <strong>"Bolões Públicos"</strong> e veja a lista de bolões abertos. Os bolões públicos são criados pela própria administração do Bolão Elite — cada um tem regras próprias (competição, sistema de pontuação, eventual premiação em concurso cultural). Toque em "Participar" e pronto. Não há limite de bolões públicos em que você pode entrar.
              </Trans>
            </p>
          </section>

          <section id="criar-bolao">
            <h2><Icon icon={<Users className="w-6 h-6" />} /> 3. {t('howtoplay.step_3', 'Criando seu próprio bolão')}</h2>
            <p>
              <Trans i18nKey="howtoplay.s3_p1" t={t}>
                Quer organizar a competição entre o seu grupo? Os bolões que você cria são <strong>privados</strong> — só entra quem você convida. (Bolões públicos ficam a cargo da administração do Bolão Elite.) Criar leva menos de um minuto: no app ou site, toque em <strong>"Criar Bolão"</strong> e preencha:
              </Trans>
            </p>
            <ol>
              <li><strong>{t('howtoplay.s3_li1_k', 'Nome do bolão:')}</strong> {t('howtoplay.s3_li1_v', 'algo memorável como "Bolão do Trampo 2026" ou "Galera da Faculdade".')}</li>
              <li><strong>{t('howtoplay.s3_li2_k', 'Campeonato:')}</strong> {t('howtoplay.s3_li2_v', 'escolha entre Copa do Mundo 2026, Brasileirão, Copa do Brasil, Libertadores, Champions, Carioca, Paulista e outros estaduais.')}</li>
              <li><strong>{t('howtoplay.s3_li3_k', 'Regra de pontuação:')}</strong> {t('howtoplay.s3_li3_v', 'escolha entre as 3 regras disponíveis (A, B ou C — explicadas mais abaixo).')}</li>
              <li><strong>{t('howtoplay.s3_li4_k', 'Tempo de bloqueio:')}</strong> {t('howtoplay.s3_li4_v', 'defina se os palpites travam 10, 30 ou 60 minutos antes de cada jogo.')}</li>
              <li><strong>{t('howtoplay.s3_li5_k', 'Palpites ocultos:')}</strong> {t('howtoplay.s3_li5_v', 'escolha se os participantes veem os palpites uns dos outros antes do jogo ou não.')}</li>
            </ol>
            <p>{t('howtoplay.s3_p2', 'Confirmou? Pronto. O bolão já está ativo e pronto para receber participantes.')}</p>
          </section>

          <section id="convidar">
            <h2><Icon icon={<Users className="w-6 h-6" />} /> 4. {t('howtoplay.step_4', 'Convidando amigos')}</h2>
            <p>
              <Trans i18nKey="howtoplay.s4_p1" t={t}>
                Em bolões privados, abra o bolão e toque em <strong>"Convidar"</strong>. Você vê um link único — algo como <code className="bg-gray-100 px-2 py-1 rounded text-xs">bolaoelite.com.br/join/abc123</code>. Copie e cole no WhatsApp do grupo, no e-mail ou nas Stories do Instagram.
              </Trans>
            </p>
            <p>{t('howtoplay.s4_p2', 'Quem clicar e ainda não tiver conta cria uma na hora e já entra no bolão. Quem já tem conta entra com um clique. Sem cadastro chato.')}</p>
          </section>

          <section id="palpitar">
            <h2><Icon icon={<Target className="w-6 h-6" />} /> 5. {t('howtoplay.step_5', 'Fazendo palpites')}</h2>
            <p>
              <Trans i18nKey="howtoplay.s5_p1" t={t}>
                Esse é o coração do bolão. Em cada rodada, abra o bolão e veja a lista de partidas da próxima rodada. Para cada partida, informe <strong>o placar exato que você acredita que vai acontecer</strong> — por exemplo, "Brasil 2 x 1 Argentina".
              </Trans>
            </p>
            <p>
              <Trans i18nKey="howtoplay.s5_p2" t={t}>
                <strong>Prazo limite:</strong> palpites são bloqueados automaticamente <strong>antes</strong> do início da partida. O criador do bolão escolhe o tempo de bloqueio entre <strong>10, 30 ou 60 minutos</strong>. Depois disso, não dá mais para alterar nem cadastrar palpite — por isso vale a pena ativar as notificações de lembrete.
              </Trans>
            </p>
            <h3>{t('howtoplay.s5_h_hidden', 'Palpites ocultos')}</h3>
            <p>{t('howtoplay.s5_p3', 'O criador do bolão pode ativar a opção de ocultar os palpites: com ela, ninguém vê o palpite dos outros até a partida começar. Deixa o jogo mais justo e a revelação mais divertida. É uma configuração do bolão — disponível para qualquer bolão, sem custo.')}</p>
          </section>

          <section id="pontuacao">
            <h2><Icon icon={<BarChart3 className="w-6 h-6" />} /> 6. {t('howtoplay.step_6', 'Como funciona a pontuação')}</h2>
            <p>
              <Trans i18nKey="howtoplay.s6_p1" t={t}>
                O Bolão Elite oferece <strong>3 regras de pontuação</strong>. O criador do bolão escolhe qual usar no momento da criação — todas valem ao longo do bolão inteiro.
              </Trans>
            </p>
            <h3>{t('howtoplay.s6_ha', 'Regra A — Simples')}</h3>
            <ul>
              <li><strong>{t('howtoplay.s6_a_li1_k', 'Placar exato:')}</strong> {t('howtoplay.s6_a_li1_v', '1 ponto')}</li>
              <li><strong>{t('howtoplay.s6_a_li2_k', 'Qualquer outra opção:')}</strong> {t('howtoplay.s6_a_li2_v', '0 pontos')}</li>
            </ul>
            <p>{t('howtoplay.s6_a_p', 'Ideal para grupos que querem foco total no acerto exato. Sem prêmio por chegar perto.')}</p>

            <h3>{t('howtoplay.s6_hb', 'Regra B — Intermediária (popular)')}</h3>
            <ul>
              <li><strong>{t('howtoplay.s6_b_li1_k', 'Placar exato:')}</strong> {t('howtoplay.s6_b_li1_v', '3 pontos')}</li>
              <li><strong>{t('howtoplay.s6_b_li2_k', 'Vencedor ou empate correto:')}</strong> {t('howtoplay.s6_b_li2_v', '1 ponto')}</li>
              <li><strong>{t('howtoplay.s6_b_li3_k', 'Qualquer outra opção:')}</strong> {t('howtoplay.s6_b_li3_v', '0 pontos')}</li>
            </ul>
            <p>{t('howtoplay.s6_b_p', 'É a regra mais escolhida — premia o placar exato mas reconhece quem acertou pelo menos quem ganhou.')}</p>

            <h3>{t('howtoplay.s6_hc', 'Regra C — Avançada')}</h3>
            <ul>
              <li><strong>{t('howtoplay.s6_c_li1_k', 'Placar exato:')}</strong> {t('howtoplay.s6_c_li1_v', '10 pontos')}</li>
              <li><strong>{t('howtoplay.s6_c_li2_k', 'Vencedor ou empate correto:')}</strong> {t('howtoplay.s6_c_li2_v', '5 pontos')}</li>
              <li><strong>{t('howtoplay.s6_c_li3_k', 'Número de gols de um dos times correto:')}</strong> {t('howtoplay.s6_c_li3_v', '2 pontos')}</li>
              <li><strong>{t('howtoplay.s6_c_li4_k', 'Qualquer outra opção:')}</strong> {t('howtoplay.s6_c_li4_v', '0 pontos')}</li>
            </ul>
            <p>{t('howtoplay.s6_c_p', 'Mais granular — quem chega perto do placar leva pontos parciais. Mantém todo mundo na briga até o fim.')}</p>

            <h3>{t('howtoplay.s6_htie', 'Critérios de desempate')}</h3>
            <p>{t('howtoplay.s6_tie_p', 'Quando dois participantes empatam em pontos totais, o ranking aplica esta ordem:')}</p>
            <ol>
              <li><Trans i18nKey="howtoplay.s6_tie_li1" t={t}>Maior número de <strong>placares exatos</strong></Trans></li>
              <li><Trans i18nKey="howtoplay.s6_tie_li2" t={t}>Maior número de <strong>vencedores corretos</strong></Trans></li>
              <li><Trans i18nKey="howtoplay.s6_tie_li3" t={t}>Menor <strong>diferença agregada de gols</strong> (entre palpites e resultados)</Trans></li>
              <li>{t('howtoplay.s6_tie_li4', 'Ordem alfabética por nome (desempate final)')}</li>
            </ol>

            <h3>{t('howtoplay.s6_faq_h3', 'Perguntas frequentes sobre a pontuação')}</h3>
            <p>{t('howtoplay.s6_faq_p', 'A dúvida mais comum é se os pontos somam. Não somam — em cada partida vale só a melhor faixa que o seu palpite atingiu. Veja os casos mais comuns:')}</p>
            <ScoringFAQ className="mt-2" />
          </section>

          <section id="ranking">
            <h2><Icon icon={<Trophy className="w-6 h-6" />} /> 7. {t('howtoplay.step_7', 'Acompanhando o ranking')}</h2>
            <p>{t('howtoplay.s7_p1', 'Conforme os jogos terminam, a pontuação de cada participante é calculada e o ranking atualiza em tempo real. Não precisa esperar o administrador "fechar a rodada" — assim que o jogo acaba e o resultado oficial é importado da API de futebol, os pontos caem na sua tela.')}</p>
            <p>{t('howtoplay.s7_p2', 'Você pode ver o ranking por:')}</p>
            <ul>
              <li><strong>{t('howtoplay.s7_li1_k', 'Geral:')}</strong> {t('howtoplay.s7_li1_v', 'soma de todas as rodadas até agora.')}</li>
              <li><strong>{t('howtoplay.s7_li2_k', 'Rodada:')}</strong> {t('howtoplay.s7_li2_v', 'ranking só da última rodada — útil pra ver quem está em alta.')}</li>
              <li><strong>{t('howtoplay.s7_li3_k', 'Fase:')}</strong> {t('howtoplay.s7_li3_v', 'em campeonatos com fase de grupos + mata-mata, há rankings separados.')}</li>
            </ul>
            <p>
              <Trans i18nKey="howtoplay.s7_p3" t={t}>
                Também há o <strong>Ranking Global</strong>: ranking entre todos os usuários do Bolão Elite em uma competição (ex: quem é o melhor palpiteiro do Brasileirão 2026 considerando todos que jogam na plataforma).
              </Trans>
            </p>
          </section>

          <section id="conquistas">
            <h2><Icon icon={<Award className="w-6 h-6" />} /> 8. {t('howtoplay.step_8', 'Conquistas e estatísticas')}</h2>
            <p>
              <Trans i18nKey="howtoplay.s8_p1" t={t}>
                Além dos pontos, o Bolão Elite te recompensa com <strong>conquistas</strong>: emblemas que você desbloqueia ao atingir marcos. Acertou 5 placares exatos seguidos? Ganhou conquista. Liderou o ranking por uma rodada inteira? Ganhou outra. Cada conquista vira parte do seu histórico no perfil.
              </Trans>
            </p>
            <p>
              <Trans i18nKey="howtoplay.s8_p2" t={t}>
                As <strong>estatísticas</strong> mostram seu desempenho ao longo do tempo: taxa de acerto de placar exato, média de pontos por rodada, comparativo com outros participantes do bolão. É um jeito de ver onde você é forte e onde dá pra melhorar.
              </Trans>
            </p>
          </section>

          <section id="casos-especiais">
            <h2><Icon icon={<AlertCircle className="w-6 h-6" />} /> 9. {t('howtoplay.step_9', 'Casos especiais')}</h2>
            <h3>{t('howtoplay.s9_h1', 'Jogo adiado ou cancelado')}</h3>
            <p>{t('howtoplay.s9_p1', 'Se a partida é adiada para outra data, seu palpite continua valendo na nova data. Se o jogo é definitivamente cancelado (raro), o palpite é anulado e ninguém ganha nem perde pontos por ele.')}</p>
            <h3>{t('howtoplay.s9_h2', 'Palpite tardio')}</h3>
            <p>{t('howtoplay.s9_p2', 'Se você esqueceu de palpitar antes do início do jogo, a partida fica registrada como "não palpitada" e você não ganha nem perde pontos. Não há palpite automático nem multa.')}</p>
            <h3>{t('howtoplay.s9_h3', 'Time que mudou de nome')}</h3>
            <p>{t('howtoplay.s9_p3', 'O Bolão Elite sincroniza com a API oficial de futebol, então mudanças de nome de time são refletidas automaticamente. Se o seu time muda de nome no meio do campeonato, seus palpites antigos seguem válidos.')}</p>
            <h3>{t('howtoplay.s9_h4', 'Erro no resultado importado')}</h3>
            <p>
              <Trans i18nKey="howtoplay.s9_p4" t={t}>
                Se você notar que o resultado oficial registrado está errado, entre em contato pelo <Link to="/contato" className="text-primary-600 font-semibold hover:underline">canal oficial</Link>. Corrigimos manualmente e recalculamos a rodada.
              </Trans>
            </p>
            <h3>{t('howtoplay.s9_h5', 'Placar que vale: tempo normal + prorrogação')}</h3>
            <p>{t('howtoplay.s9_p5', 'O placar que conta para os seus pontos é o resultado ao final do tempo normal somado à prorrogação, quando houver. A disputa de pênaltis não é considerada: em jogos decididos nos pênaltis, vale o placar em que a partida terminou empatada, antes das cobranças.')}</p>
          </section>

          <section id="premium">
            <h2><Icon icon={<Crown className="w-6 h-6" />} /> 10. {t('howtoplay.step_10', 'Free vs Premium')}</h2>
            <p>
              <Trans
                i18nKey="howtoplay.s10_p1"
                t={t}
                values={{
                  count: limits.maxPrivatePoolsCreated,
                  noun: poolNoun,
                  members: limits.maxPrivatePoolMembers,
                  joined: limits.maxPrivatePoolsJoined,
                }}
              >
                <strong>Plano gratuito:</strong> dá pra jogar bolão completo. Você cria {'{{count}}'} {'{{noun}}'} (com até {'{{members}}'} participantes), entra em até {'{{joined}}'} bolões privados de amigos e participa de bolões públicos sem limite — palpitando em todas as competições, com ranking, conquistas e notificações.
              </Trans>
            </p>
            <p>
              <Trans
                i18nKey="howtoplay.s10_p2"
                t={t}
                values={{ price: priceLabel }}
                defaults="<0>Plano Premium ({{price}}/mês):</0> remove os limites do plano gratuito:"
              >
                <strong>Plano Premium ({priceLabel}/mês):</strong> remove os limites do plano gratuito:
              </Trans>
            </p>
            <ul>
              <li>{t('howtoplay.s10_li1', 'Bolões privados ilimitados, com participantes ilimitados')}</li>
              <li>{t('howtoplay.s10_li2', 'Entrar em quantos bolões de outras pessoas você quiser')}</li>
              <li>{t('howtoplay.s10_li3', 'Quem você convida não precisa ser Premium')}</li>
              <li>{t('howtoplay.s10_li4', 'Navegação sem anúncios')}</li>
              <li>{t('howtoplay.s10_li5', 'Prioridade em novas funcionalidades')}</li>
            </ul>
            <p>
              <Trans i18nKey="howtoplay.s10_p3" t={t}>
                Detalhes e preço em <Link to="/premium" className="text-primary-600 font-semibold hover:underline">/premium</Link> e <Link to="/precos" className="text-primary-600 font-semibold hover:underline">/precos</Link>.
              </Trans>
            </p>
          </section>
        </article>

        {/* CTA final */}
        <section className="mt-16 bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-6 sm:p-8 text-white text-center">
          <Bell className="w-10 h-10 mx-auto mb-3 opacity-80" />
          <h2 className="text-xl sm:text-2xl font-extrabold mb-2">{t('howtoplay.cta_title', 'Pronto pra começar?')}</h2>
          <p className="text-white/80 mb-5 max-w-2xl mx-auto">
            {t('howtoplay.cta_text', 'Crie sua conta gratuita e teste o Bolão Elite em menos de 5 minutos. Sem pegadinha, sem cartão.')}
          </p>
          <a
            href={loginUrl}
            className="inline-block px-8 py-3 bg-white text-primary-700 font-bold rounded-lg hover:bg-primary-50 transition-colors"
          >
            {t('howtoplay.cta_button', 'Criar conta gratuita')}
          </a>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}

function Icon({ icon }: { icon: React.ReactNode }) {
  return (
    <span className="w-10 h-10 bg-primary-100 text-primary-700 rounded-xl flex items-center justify-center flex-shrink-0">
      {icon}
    </span>
  );
}
